/**
 * Aufgaben im Kalender einplanen - in beide Richtungen (Roadmap F-06).
 *
 * Eine Aufgabe hat zwei getrennte Daten: die Deadline (F-09, bis wann fertig)
 * und den EINGEPLANTEN Zeitpunkt (wann ich daran arbeite). Nur um Letzteren
 * geht es hier:
 *
 *   nur Datum            -> `plannedDate`, erscheint oben am Tag im Kalender
 *   Datum + Uhrzeit      -> zusaetzlich ein verknuepfter Zeitblock (Termin)
 *
 * Aufgabe und Zeitblock sind ueber `Task.timeBlockEventId` und
 * `CalendarEvent.sourceTaskId` gekoppelt. Wer eine Seite aendert, zieht die
 * andere in DERSELBEN Transaktion nach - `scheduleTask` von der Aufgabe aus,
 * `syncTasksWithEvents` vom Kalender aus.
 */
import { db } from '@/db/schema';
import { datePart, nowInstant } from '@/lib/datetime';
import { shiftFloating } from '@/lib/recurrence/edit';
import { newId } from '@/lib/utils/id';
import type { ID, LocalDate, LocalDateTime, Task } from '@/domain/types';

export interface TaskPlan {
  /** `null` nimmt die Aufgabe aus der Planung. */
  date: LocalDate | null;
  /** `'HH:mm'` - ohne Uhrzeit kein Zeitblock. */
  start?: string;
  /** Dauer des Zeitblocks. */
  minutes?: number;
}

export const DEFAULT_BLOCK_MINUTES = 60;

/** Status nach dem Einplanen bzw. Herausnehmen - Erledigtes bleibt erledigt. */
function statusFuer(task: Task, geplant: boolean): Task['status'] {
  if (task.status === 'erledigt') return task.status;
  if (geplant) return 'geplant';
  return task.status === 'geplant' ? 'naechste' : task.status;
}

export async function scheduleTask(taskId: ID, plan: TaskPlan): Promise<void> {
  await db.transaction('rw', db.tasks, db.events, async () => {
    const task = await db.tasks.get(taskId);
    if (!task) return;
    const ts = nowInstant();
    const block = task.timeBlockEventId ? await db.events.get(task.timeBlockEventId) : undefined;

    // Ohne Uhrzeit (oder ohne Datum) gibt es keinen Zeitblock mehr. Der Block
    // war die Planung dieser Aufgabe - er verschwindet mit ihr.
    if (!plan.date || !plan.start) {
      if (block) await db.events.delete(block.id);
      await db.tasks.put(ohneUndefiniert({
        ...task,
        plannedDate: plan.date ?? undefined,
        timeBlockEventId: undefined,
        status: statusFuer(task, Boolean(plan.date)),
        isBigThree: plan.date ? task.isBigThree : false,
        updatedAt: ts,
      }));
      return;
    }

    const minutes = Math.max(5, plan.minutes ?? task.estimateMinutes ?? DEFAULT_BLOCK_MINUTES);
    const start: LocalDateTime = `${plan.date}T${plan.start}`;
    // Offsetfrei gerechnet - auch am Tag der Zeitumstellung genau `minutes` lang.
    const end = shiftFloating(start, minutes * 60_000);

    let eventId = block?.id;
    if (block) {
      await db.events.put({ ...block, start, end, title: task.title, updatedAt: ts });
    } else {
      eventId = newId();
      await db.events.add({
        id: eventId, areaId: task.areaId, title: task.title, allDay: false, start, end,
        tz: 'Europe/Berlin', status: 'confirmed', isRecurring: 0, sourceTaskId: taskId,
        createdAt: ts, updatedAt: ts,
      });
    }

    await db.tasks.put({
      ...task,
      plannedDate: plan.date,
      timeBlockEventId: eventId,
      estimateMinutes: minutes,
      status: statusFuer(task, true),
      updatedAt: ts,
    });
  });
}

/**
 * Vom Kalender aus: wurde ein Zeitblock verschoben oder geloescht, folgt die
 * Aufgabe. Aufrufen INNERHALB der Schreibtransaktion der Termine (mit
 * `db.tasks` im Umfang), mit allen betroffenen Termin-IDs.
 */
export async function syncTasksWithEvents(eventIds: ID[]): Promise<void> {
  if (eventIds.length === 0) return;
  const ids = new Set(eventIds);
  const betroffen = await db.tasks.filter((t) => Boolean(t.timeBlockEventId && ids.has(t.timeBlockEventId))).toArray();
  const ts = nowInstant();

  for (const task of betroffen) {
    const block = await db.events.get(task.timeBlockEventId!);
    if (!block) {
      // Block geloescht: die Aufgabe bleibt am Tag eingeplant, nur ohne Uhrzeit.
      await db.tasks.put(ohneUndefiniert({ ...task, timeBlockEventId: undefined, updatedAt: ts }));
      continue;
    }
    const tag = datePart(String(block.start));
    if (tag !== task.plannedDate) {
      await db.tasks.put({ ...task, plannedDate: tag, status: statusFuer(task, true), updatedAt: ts });
    }
  }
}

/** Ein `put` soll entfernte Felder wirklich entfernen, nicht als `undefined` speichern. */
function ohneUndefiniert<T extends object>(row: T): T {
  const copy = { ...row } as Record<string, unknown>;
  for (const key of Object.keys(copy)) if (copy[key] === undefined) delete copy[key];
  return copy as T;
}
